import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown'; // <-- IMPORTA LA NUOVA LIBRERIA
import './EditableContentMultilang.css';

const EditIcon = () => <span className="edit-icon">✏️</span>;
const languages = ['it', 'en', 'de', 'nl', 'fr'];

const EditableContentMultilang = ({ docPath, field, elementType = 'p', placeholder = '' }) => {
    const { currentUser } = useAuth();
    const { i18n } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [contentMap, setContentMap] = useState({});
    const [originalContentMap, setOriginalContentMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadContent = async () => {
            setLoading(true);
            setError(null);
            try {
                const docRef = doc(db, docPath);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && docSnap.data()[field]) {
                    const data = docSnap.data()[field];
                    setContentMap(typeof data === 'object' ? data : { it: data });
                    setOriginalContentMap(typeof data === 'object' ? data : { it: data });
                } else {
                    const defaultMap = { it: placeholder || `[Modifica ${field}]` };
                    setContentMap(defaultMap);
                    setOriginalContentMap(defaultMap);
                }
            } catch (err) {
                setError("Errore caricamento");
                setContentMap({ it: "[Errore]" });
            } finally {
                setLoading(false);
            }
        };
        loadContent();
    }, [docPath, field, placeholder]);

    const handleSave = async () => {
        if (!currentUser) return;
        const docRef = doc(db, docPath);
        const cleanedContentMap = Object.entries(contentMap).reduce((acc, [lang, text]) => {
            if (text && text.trim() !== '') { acc[lang] = text; }
            return acc;
        }, {});

        const dataToSave = {
            [field]: cleanedContentMap,
            [`${field}_lastUpdatedBy`]: currentUser.email,
            updatedAt: serverTimestamp()
        };

        try {
            await setDoc(docRef, dataToSave, { merge: true });
            setOriginalContentMap(cleanedContentMap);
            setIsEditing(false);
            setError(null);
        } catch (err) {
            setError("Errore salvataggio");
            alert("Errore durante il salvataggio.");
        }
    };

    const handleCancel = () => {
        setContentMap(originalContentMap);
        setIsEditing(false);
        setError(null);
    }

    const handleInputChange = (lang, value) => {
        setContentMap(prev => ({ ...prev, [lang]: value }));
    };

    if (loading) return <span className="loading-placeholder">...</span>;
    if (error) return <span className="error-placeholder">{error}</span>;

    if (isEditing && currentUser) {
        // AUMENTIAMO LE RIGHE DELLA TEXTAREA PER FACILITARE LA SCRITTURA
        const rows = (elementType === 'textarea') ? 20 : 5; 
        
        return (
            <div className="editable-container editing multilang-editor">
                {languages.map(lang => (
                    <div key={lang} className="multilang-editor-group">
                        <label>{lang.toUpperCase()}</label>
                        <textarea
                            value={contentMap[lang] || ''}
                            onChange={(e) => handleInputChange(lang, e.target.value)}
                            rows={rows} // Usa il numero di righe calcolato
                        />
                    </div>
                ))}
                <div className="editor-actions">
                    <button onClick={handleSave}>Salva</button>
                    <button onClick={handleCancel}>Annulla</button>
                </div>
            </div>
        );
    }

    const lang = i18n.language;
    const fallbackLang = 'it';
    const textToDisplay = contentMap?.[lang] || contentMap?.[fallbackLang] || contentMap?.it || placeholder || '';
    
    const Element = elementType === 'textarea' ? 'div' : elementType;
    const containerClass = currentUser ? 'editable-container logged-in' : 'editable-container';

    // --- MODIFICA CHIAVE QUI ---
    // Se l'elemento è una textarea, usa ReactMarkdown per renderizzare.
    // Altrimenti, usa il comportamento normale per titoli e paragrafi semplici.
    const content = elementType === 'textarea' 
        ? <div className="markdown-display" style={{ whiteSpace: 'pre-wrap' }}><ReactMarkdown>{textToDisplay}</ReactMarkdown></div> 
        : <Element>{textToDisplay}</Element>;

    return (
        <div className={containerClass}>
            {content}
            {currentUser && <span onClick={() => setIsEditing(true)}><EditIcon /></span>}
        </div>
    );
};

export default EditableContentMultilang;